import type { RetrievePriceQuoteResponse } from '../../../../../../services/oneButtonServices/quoteServices';
import type { RetrieveSystemDesignResponse } from '../../../../../../services/oneButtonServices/designServices';
import { getRoofSquaresFromSystemDesign } from '../../../../../../services/oneButtonServices/designServices';
import type { PriceQuoteOverrides } from '../../../../../../store/oneButtonStore';

export type ProductQuoteKind = 'solar' | 'roof' | 'battery';

export type LinkedProductQuoteValues = {
    totalPrice: number | null;
    epcAmount: number | null;
    epcUnit: 'usd' | 'per_watt' | 'per_sqft';
    /** $/kWh when pricing uses a solar rate (linked quote / agreement). */
    solarRateKwh: number | null;
    quoteId: string | null;
    isLinkedAgreement: boolean;
};

function normPartner(v: string): string {
    return String(v || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
}

function truthyFlag(v: unknown): boolean {
    return v === true || v === 'true' || v === 1 || v === '1';
}

function quoteRowLinkedToAgreement(row: Record<string, unknown>): boolean {
    if (truthyFlag(row.Selected ?? row.selected)) return true;
    if (truthyFlag(row.isMovedToContract ?? row.IsMovedToContract ?? row.IsMove_To_Contract)) return true;
    if (truthyFlag(row.hasContract ?? row.HasContract)) return true;
    const fps = row.Finance_Partner_Contract_Status ?? row.finance_Partner_Contract_Status;
    const fpsNorm = String(fps ?? '').trim().toLowerCase();
    if (
        fps != null &&
        fpsNorm !== '' &&
        fpsNorm !== 'null' &&
        fpsNorm !== 'voided' &&
        fpsNorm !== 'void' &&
        fpsNorm !== 'canceled' &&
        fpsNorm !== 'cancelled'
    ) {
        return true;
    }
    const fid = row.Finance_Partner_Contract_Id ?? row.finance_Partner_Contract_Id;
    if (fid != null && String(fid).trim() !== '') return true;
    const status = String(row.ContractStatus ?? row.contractStatus ?? row.ApplicationStatus ?? '').toLowerCase();
    if (status.includes('submit') || status.includes('sign')) return true;
    return false;
}

function quoteMatchesProduct(row: Record<string, unknown>, kind: ProductQuoteKind): boolean {
    const pt = String(row.ProductType ?? row.productType ?? row.Product_Type_Name ?? '').trim();
    const u = pt.toLowerCase().replace(/\s+/g, '');
    if (!u) return kind === 'solar';
    if (kind === 'solar') {
        return u.includes('solar') || u === 's' || u === 'so' || u === 'sr' || u === 'sb' || u === 'sbr';
    }
    if (kind === 'roof') {
        return u.includes('roof') || u === 'r' || u === 'ro' || u === 'sr' || u === 'sbr';
    }
    return u.includes('battery') || u === 'b' || u === 'bo' || u === 'sb' || u === 'br' || u === 'sbr';
}

function firstPositiveNumber(...vals: unknown[]): number | null {
    for (const v of vals) {
        if (v == null || v === '') continue;
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
}

function solarRateFromCombinedRate(row: Record<string, unknown>): number | null {
    const cr = row.CombinedRate ?? row.combinedRate;
    if (!cr || typeof cr !== 'object') return null;
    const rates = (cr as Record<string, unknown>).Rates ?? (cr as Record<string, unknown>).rates;
    if (!Array.isArray(rates)) return null;
    for (const item of rates) {
        if (!item || typeof item !== 'object') continue;
        const r = item as Record<string, unknown>;
        const name = String(r.Name ?? r.name ?? '').toLowerCase();
        if (!name.includes('solar')) continue;
        const n = firstPositiveNumber(r.Rate, r.rate, r.Value, r.value);
        if (n != null) return n;
    }
    return null;
}

/** $/kWh from a RetrievePriceQuote QuoteVersion solar row. */
function solarRateKwhFromQuoteRow(row: Record<string, unknown>): number | null {
    const fromCombined = solarRateFromCombinedRate(row);
    if (fromCombined != null) return fromCombined;

    const direct = firstPositiveNumber(
        row.ProposedSolarRate,
        row.Proposed_Rate,
        row.ProposedRate,
        row.SolarRate,
        row.Solar_Rate,
        row.solarRate,
    );
    if (direct != null) return direct;

    const pm = String(row.PricingMethod ?? row.pricingMethod ?? row.Quote_PricingMethod ?? '').toLowerCase();
    if (pm.includes('rate') && !pm.includes('ppw')) {
        return firstPositiveNumber(row.PricingValue, row.pricingValue);
    }
    return null;
}

export function formatSolarRateKwh(rate: number | null | undefined): string {
    if (rate == null || !Number.isFinite(rate) || rate <= 0) return '—';
    return `$${rate.toFixed(3)} /kWh`;
}

/** Best available solar rate ($/kWh) for dashboard cards — quote, overrides, utility, price quote API. */
export function resolveSolarRateKwh(options: {
    linkedSolarQuote: LinkedProductQuoteValues | null;
    priceQuoteOverrides: PriceQuoteOverrides | null;
    priceQuoteDetails: RetrievePriceQuoteResponse | null | undefined;
    utilitySolar: Record<string, unknown>;
}): number | null {
    const { linkedSolarQuote, priceQuoteOverrides, priceQuoteDetails, utilitySolar } = options;

    if (linkedSolarQuote?.solarRateKwh != null) return linkedSolarQuote.solarRateKwh;

    const fromOverrides = firstPositiveNumber(priceQuoteOverrides?.proposedSolarRate);
    if (fromOverrides != null) return fromOverrides;

    const pq = priceQuoteDetails as Record<string, unknown> | null | undefined;
    if (pq) {
        const fromPq = firstPositiveNumber(
            pq.ProposedRate,
            pq.Proposed_Solar_Rate,
            pq.ProposedSolarRate,
            pq.SolarRate,
            pq.MinRate,
        );
        if (fromPq != null) return fromPq;
    }

    return firstPositiveNumber(
        utilitySolar.Rate,
        utilitySolar.SolarRate,
        utilitySolar.UtilityRate,
        utilitySolar.ProposedRate,
        utilitySolar.Proposed_Solar_Rate,
        utilitySolar.MinRate,
    );
}

function oppProductRow(opp: any, productName: string): any | null {
    const rows = opp?.OpportunityProduct ?? opp?.opportunityProduct ?? [];
    if (!Array.isArray(rows)) return null;
    return (
        rows.find(
            (p: any) =>
                String(p?.Product_Type_Name ?? p?.product_Type_Name ?? '').toLowerCase() ===
                productName.toLowerCase(),
        ) ?? null
    );
}

function partnerFromOppRow(row: any | null): string {
    if (!row) return '';
    return String(row.Finance_Partner_Display_Name ?? row.Finance_Partner ?? row.finance_Partner ?? '').trim();
}

function pickLinkedQuoteRow(
    kind: ProductQuoteKind,
    opportunityDetails: any,
    priceQuoteDetails: RetrievePriceQuoteResponse | null | undefined,
): Record<string, unknown> | null {
    const versions = priceQuoteDetails?.QuoteVersion;
    if (!Array.isArray(versions) || versions.length === 0) return null;

    const productName = kind === 'solar' ? 'Solar' : kind === 'roof' ? 'Roof' : 'Battery';
    const oppRow = oppProductRow(opportunityDetails, productName);
    const wantPartner = normPartner(partnerFromOppRow(oppRow));

    const candidates = versions
        .filter((v): v is Record<string, unknown> => !!v && typeof v === 'object')
        .filter((row) => quoteMatchesProduct(row, kind))
        .filter((row) => {
            if (!wantPartner) return true;
            const fp = normPartner(String(row.FinancePartner ?? row.financePartner ?? ''));
            return !fp || fp === wantPartner;
        });

    if (candidates.length === 0) return null;

    const scored = candidates
        .map((row) => {
            let score = 0;
            if (quoteRowLinkedToAgreement(row)) score += 1000;
            if (truthyFlag(row.IsSigned ?? row.isSigned)) score += 500;
            if (truthyFlag(row.ActiveFlag ?? row.activeFlag)) score += 50;
            const qid = Number(row.QuoteId ?? row.quoteId);
            if (Number.isFinite(qid)) score += qid * 0.001;
            return { row, score };
        })
        .sort((a, b) => b.score - a.score);

    return scored[0]?.row ?? null;
}

function extractLinkedValues(row: Record<string, unknown>, kind: ProductQuoteKind): LinkedProductQuoteValues {
    const quoteId = String(row.QuoteId ?? row.quoteId ?? '').trim() || null;
    const linked = quoteRowLinkedToAgreement(row);
    if (kind === 'solar') {
        const totalPrice = firstPositiveNumber(
            row.TotalLoanEPC,
            row.TotalEPC,
            row.TrinityLoanEPC,
            row.TrinityEPC,
            row.Dealer_Epc,
            row.DealerEPC,
            row.grossSalesPrice,
            row.Gross_Sales_Price,
        );
        const epcAmount = firstPositiveNumber(row.EpcPerWattPvOnly, row.Desired_PPW_Net, row.EPCPrice, row.PPW);
        const solarRateKwh = solarRateKwhFromQuoteRow(row);
        return {
            totalPrice,
            epcAmount,
            epcUnit: 'per_watt',
            solarRateKwh,
            quoteId,
            isLinkedAgreement: linked,
        };
    }

    if (kind === 'roof') {
        /** Total contract EPC from quote/agreement — not squares × $/sq ft. */
        const epcTotal = firstPositiveNumber(
            row.TrinityLoanEPC,
            row.TotalLoanEPC,
            row.TrinityEPC,
            row.TotalEPC,
            row.Dealer_Epc,
            row.DealerEPC,
            row.RR_Price,
            row.Roof_Price_Sub_Total,
            row.grossSalesPrice,
            row.Gross_Sales_Price,
        );
        const epcPerSqft = firstPositiveNumber(
            row.EpcPerSqFt,
            row.EPCPrice,
            row.EpcPrice,
            row.Roof_EPC,
        );
        if (epcTotal != null) {
            return {
                totalPrice: null,
                epcAmount: epcTotal,
                epcUnit: 'usd',
                solarRateKwh: null,
                quoteId,
                isLinkedAgreement: linked,
            };
        }
        if (epcPerSqft != null) {
            return {
                totalPrice: null,
                epcAmount: epcPerSqft,
                epcUnit: 'per_sqft',
                solarRateKwh: null,
                quoteId,
                isLinkedAgreement: linked,
            };
        }
        return {
            totalPrice: null,
            epcAmount: null,
            epcUnit: 'usd',
            solarRateKwh: null,
            quoteId,
            isLinkedAgreement: linked,
        };
    }

    const totalPrice = firstPositiveNumber(
        row.Battery_Price_Sub_Total,
        row.battery_Price_Sub_Total,
        row.TotalLoanEPC,
        row.TotalEPC,
        row.TrinityLoanEPC,
        row.grossSalesPrice,
    );
    /** Signed contract battery subtotal — not combined SB TotalEPC (solar + battery). */
    const epcAmount = firstPositiveNumber(
        row.Battery_Price_Sub_Total,
        row.battery_Price_Sub_Total,
        row.TotalLoanEPC,
        row.TrinityLoanEPC,
        row.TrinityEPC,
        row.Dealer_Epc,
        row.DealerEPC,
        row.EPCPrice,
        row.TotalEPC,
    );

    return {
        totalPrice,
        epcAmount,
        epcUnit: 'usd',
        solarRateKwh: null,
        quoteId,
        isLinkedAgreement: linked,
    };
}

export function getLinkedProductQuoteValues(
    kind: ProductQuoteKind,
    opportunityDetails: any,
    priceQuoteDetails: RetrievePriceQuoteResponse | null | undefined,
): LinkedProductQuoteValues | null {
    const row = pickLinkedQuoteRow(kind, opportunityDetails, priceQuoteDetails);
    if (!row) return null;
    return extractLinkedValues(row, kind);
}

export function formatMetricTotalPrice(amount: number | null, _fromQuote?: boolean): string {
    if (amount == null || !Number.isFinite(amount)) return '—';
    return `$${Number(amount).toFixed(2).toLocaleString()}`;
}

/** Solar card EPC — linked quote only (no viewer PPW fallback). */
export function formatSolarEpcFromQuote(linked: LinkedProductQuoteValues | null): string {
    if (!linked || linked.epcAmount == null) return '—';
    return formatMetricEpc(linked.epcAmount, linked.epcUnit, linked.isLinkedAgreement);
}

export function formatMetricEpc(
    amount: number | null,
    unit: LinkedProductQuoteValues['epcUnit'],
    fromQuote: boolean,
): string {
    void fromQuote;
    if (amount == null) return '—';
    if (unit === 'per_watt') return `$${amount.toFixed(2)} /W`;
    if (unit === 'per_sqft') return `$${amount.toFixed(2)} /sq ft`;
    return `$${Math.round(amount).toLocaleString('en-US')}`;
}

/** Roof card EPC — quote/agreement only (no design-derived fallback). */
export function formatRoofEpcFromQuote(linked: LinkedProductQuoteValues | null): string {
    if (!linked || linked.epcAmount == null) return '—';
    return formatMetricEpc(linked.epcAmount, linked.epcUnit, linked.isLinkedAgreement);
}

/** Roof squares: overrides (UI) → price quote → design Roof / RoofSquare fields. */
export type BatteryDesignDisplay = {
    manufacturer: string;
    modelDisplay: string;
    quantity: string | number | null;
    capacityKwh: string;
    unitPrice: string | null;
};

export function formatBatteryCount(quantity: string | number | null | undefined): string {
    if (quantity == null || quantity === '' || quantity === '—') return '—';
    const n = Number(quantity);
    if (!Number.isFinite(n) || n <= 0) return '—';
    return `${n} unit${n === 1 ? '' : 's'}`;
}

function resolveBatteryRawFields(sd: Record<string, unknown>): {
    manufacturer: string;
    rawName: string;
    quantity: string | number;
    capacity: unknown;
    unitPrice: unknown;
    power: unknown;
} {
    const batteryInfo = sd.BatteryInformation as Record<string, unknown> | undefined;
    const batteries = sd.DesignBattery ?? sd.Batteries;
    const bat0 =
        Array.isArray(batteries) && batteries.length > 0 && batteries[0] && typeof batteries[0] === 'object'
            ? (batteries[0] as Record<string, unknown>)
            : null;

    if (batteryInfo && (batteryInfo.Manufacturer || batteryInfo.Name || batteryInfo.Quantity)) {
        return {
            manufacturer: String(batteryInfo.Manufacturer ?? batteryInfo.ManufacturerName ?? '').trim(),
            rawName: String(
                batteryInfo.BatteryModelName ??
                    batteryInfo.ModelName ??
                    batteryInfo.Name ??
                    batteryInfo.Model ??
                    '',
            ).trim(),
            quantity: batteryInfo.Quantity ?? null,
            capacity: batteryInfo.Capacity ?? batteryInfo.Battery_capacity,
            unitPrice: batteryInfo.UnitPrice,
            power: batteryInfo.Power,
        };
    }

    if (bat0) {
        return {
            manufacturer: String(bat0.Manufacturer ?? bat0.ManufacturerName ?? '').trim(),
            rawName: String(bat0.BatteryModelName ?? bat0.ModelName ?? bat0.Name ?? bat0.Model ?? '').trim(),
            quantity: bat0.Quantity ?? null,
            capacity: bat0.Capacity ?? bat0.BatteryCapacity,
            unitPrice: bat0.UnitPrice,
            power: bat0.Power,
        };
    }

    const oppProducts = sd.OpportunityProductsFinanceInfo;
    if (Array.isArray(oppProducts)) {
        const row = oppProducts.find(
            (p) =>
                p &&
                typeof p === 'object' &&
                String((p as Record<string, unknown>).Product_Type_Name ?? '').toLowerCase() === 'battery',
        ) as Record<string, unknown> | undefined;
        if (row) {
            return {
                manufacturer: String(row.Manufacturer ?? row.ManufacturerName ?? '').trim(),
                rawName: String(row.BatteryModelName ?? row.ModelName ?? row.Name ?? row.Model ?? '').trim(),
                quantity: row.Quantity ?? null,
                capacity: row.Capacity,
                unitPrice: row.UnitPrice,
                power: row.Power,
            };
        }
    }

    return { manufacturer: '', rawName: '', quantity: null, capacity: null, unitPrice: null, power: null };
}

function lookupBatteryModelLabel(rawName: string, batteryModelData: unknown[] | null | undefined): string {
    const trimmed = rawName.trim();
    if (!trimmed || trimmed === '—') return '—';
    if (!Array.isArray(batteryModelData) || batteryModelData.length === 0) return trimmed;

    const found = batteryModelData.find((m) => {
        if (!m || typeof m !== 'object') return false;
        const row = m as Record<string, unknown>;
        const value = String(row.Value ?? row.value ?? row.id ?? row.ID ?? '');
        const text = String(row.Text ?? row.label ?? '');
        return value === trimmed || text === trimmed;
    });
    if (found && typeof found === 'object') {
        const row = found as Record<string, unknown>;
        return String(row.Text ?? row.label ?? trimmed).trim() || trimmed;
    }
    return trimmed;
}

/** Battery model/manufacturer/qty from system design, with master-data label lookup for numeric model ids. */
export function resolveBatteryDesignDisplay(
    systemDesign: RetrieveSystemDesignResponse | null,
    batteryModelData: unknown[] | null | undefined,
): BatteryDesignDisplay {
    const sd = (systemDesign || {}) as Record<string, unknown>;
    const { manufacturer, rawName, quantity, capacity, unitPrice, power } = resolveBatteryRawFields(sd);

    let modelDisplay = lookupBatteryModelLabel(rawName, batteryModelData);
    if (modelDisplay !== '—' && /^\d+$/.test(modelDisplay)) {
        modelDisplay = '—';
    }

    let capacityKwh = '—';
    if (capacity != null && String(capacity).trim() !== '') {
        const n = Number(capacity);
        if (Number.isFinite(n) && n > 0) capacityKwh = `${n} kWh`;
    } else if (power != null && String(power).trim() !== '') {
        const n = Number(power);
        if (Number.isFinite(n) && n > 0) capacityKwh = `${n} kW`;
    }

    return {
        manufacturer: manufacturer || '—',
        modelDisplay: modelDisplay || '—',
        quantity,
        capacityKwh,
        unitPrice:
            unitPrice != null && Number(unitPrice) > 0 ? `$${Number(unitPrice).toLocaleString('en-US')}` : null,
    };
}

export function applyBatteryDesignFallbacks(
    display: BatteryDesignDisplay,
    fallback: { manufacturer: string; name: string; quantity: number } | null,
): BatteryDesignDisplay {
    if (!fallback) return display;
    const qtyMissing =
        display.quantity == null ||
        display.quantity === '' ||
        display.quantity === '—' ||
        !Number.isFinite(Number(display.quantity)) ||
        Number(display.quantity) <= 0;
    return {
        ...display,
        manufacturer:
            display.manufacturer && display.manufacturer !== '—'
                ? display.manufacturer
                : fallback.manufacturer,
        modelDisplay:
            display.modelDisplay && display.modelDisplay !== '—'
                ? display.modelDisplay
                : fallback.name,
        quantity: qtyMissing ? fallback.quantity : display.quantity,
    };
}

export function getEffectiveRoofSquares(
    systemDesign: RetrieveSystemDesignResponse | null,
    priceQuoteOverrides: PriceQuoteOverrides | null,
    priceQuoteDetails: RetrievePriceQuoteResponse | null | undefined,
): number | null {
    const fromDesign = getRoofSquaresFromSystemDesign(systemDesign);

    const low = priceQuoteOverrides?.lowSlopeOverride;
    const normal = priceQuoteOverrides?.normalSlopeOverride;
    if (low != null || normal != null) {
        const sum = (Number(low) || 0) + (Number(normal) || 0);
        return Number.isFinite(sum) ? sum : null;
    }

    const rpqOverride = priceQuoteDetails?.RoofSquareOverride;
    if (rpqOverride != null && Number(rpqOverride) >= 0) {
        const n = Number(rpqOverride);
        if (n > 0) return n;
        if (n === 0 && fromDesign != null && fromDesign > 0) {
            // ignore 0 override if design has squares
        } else if (Number.isFinite(n)) {
            return n;
        }
    }

    const sd = systemDesign as Record<string, unknown> | null;
    const roof = sd?.Roof;
    if (roof && typeof roof === 'object' && !Array.isArray(roof)) {
        const r = roof as Record<string, unknown>;
        const ov = r.RoofSquareOverride;
        if (ov != null && Number(ov) >= 0) {
            const n = Number(ov);
            if (n > 0) return n;
            if (n === 0 && fromDesign != null && fromDesign > 0) {
                // ignore 0 override if design has squares
            } else if (Number.isFinite(n)) {
                return n;
            }
        }
    }

    if (fromDesign != null) return fromDesign;

    return null;
}
